import { useState, useEffect } from 'react'
import { useLatest } from '../useLatest'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useBruktbilFilters } from '../useBruktbilFilters'
import TopMerkerTable from '../TopMerkerTable'
import TopModellerTable from '../TopModellerTable'
import AlderStatsGrid from '../AlderStatsGrid'
import DrivstoffMatrise from '../DrivstoffMatrise'
import AldersprofilChart, { fromKjoperAlderSeries } from '../AldersprofilChart'
import MerkeMultiSelect from '../../../components/MerkeMultiSelect'
import {
  fetchKlasseMerker, fetchKlasseModeller, fetchKlasseAldersprofil,
  fetchKlasseKjoperalder, fetchKlasseDrivstoff,
  type KlasseMerkerResponse, type KlasseModellerResponse, type KlasseAldersprofilResponse,
  type KlasseKjoperAlderResponse, type KlasseDrivstoffResponse,
} from './klasse_detaljer_api'
import { fetchKlasserTable } from './api'

type Tab = 'merker' | 'modeller' | 'aldersprofil' | 'kjoperalder' | 'drivstoff'

// The two age tabs and Drivstofftype share the brand filter, so the pick
// carries across them (#586 AC5).
const MERKE_TABS: Tab[] = ['aldersprofil', 'kjoperalder', 'drivstoff']

const TABS: { slug: Tab; labelKey: `detaljTabs.${Tab}` }[] = [
  { slug: 'merker', labelKey: 'detaljTabs.merker' },
  { slug: 'modeller', labelKey: 'detaljTabs.modeller' },
  { slug: 'aldersprofil', labelKey: 'detaljTabs.aldersprofil' },
  { slug: 'kjoperalder', labelKey: 'detaljTabs.kjoperalder' },
  { slug: 'drivstoff', labelKey: 'detaljTabs.drivstoff' },
]

const _nb = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 1 })

export default function KlasseDetaljerPage() {
  const { t } = useTranslation('bruktbil')
  const { classLabel } = useParams<{ classLabel: string }>()
  const filters = useBruktbilFilters()
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState<Tab>('merker')

  const [merker, setMerker] = useState<KlasseMerkerResponse | null>(null)
  const [modeller, setModeller] = useState<KlasseModellerResponse | null>(null)
  const [aldersprofil, setAldersprofil] = useState<KlasseAldersprofilResponse | null>(null)
  const [kjoperalder, setKjoperalder] = useState<KlasseKjoperAlderResponse | null>(null)
  const [drivstoff, setDrivstoff] = useState<KlasseDrivstoffResponse | null>(null)
  const [loading, setLoading] = useState(false)
  // Brand filter for the Alder-profil tab (#482). Options come from the same
  // merker endpoint the Top merker tab uses.
  const [merkeFilter, setMerkeFilter] = useState<string[]>([])
  const [merkeOptions, setMerkeOptions] = useState<string[]>([])
  // Finansiert % (#759). Read off the Klasser grid's own response, so the KPI
  // and the grid column cannot disagree, and the server answers both from one
  // cache entry. null = still loading, or the class is not in the response.
  const [finansiert, setFinansiert] = useState<number | null>(null)

  const f = {
    from: filters.from, to: filters.to, klasse: filters.klasse,
    varebil: filters.varebil, omregistrering: filters.omregistrering,
    selgerPostnummer: filters.selgerPostnummer, kjoperPostnummer: filters.kjoperPostnummer,
  }

  useLatest(alive => {
    if (!classLabel) return
    setLoading(true)
    // Reset cached data when filters change
    setMerker(null); setModeller(null); setAldersprofil(null); setDrivstoff(null)
    const done = () => { if (alive()) setLoading(false) }
    if (tab === 'merker')
      fetchKlasseMerker(classLabel, f).then(r => { if (alive()) setMerker(r.data) }).finally(done)
    else if (tab === 'modeller')
      fetchKlasseModeller(classLabel, f).then(r => { if (alive()) setModeller(r.data) }).finally(done)
    else if (tab === 'aldersprofil')
      fetchKlasseAldersprofil(classLabel, f, merkeFilter)
        .then(r => { if (alive()) setAldersprofil(r.data) }).finally(done)
    else if (tab === 'kjoperalder')
      setLoading(false)      // fetched by the always-on effect below
    else if (tab === 'drivstoff')
      fetchKlasseDrivstoff(classLabel, f, merkeFilter).then(r => { if (alive()) setDrivstoff(r.data) }).finally(done)
    else setLoading(false)
  }, [classLabel, tab, JSON.stringify(merkeFilter), filters.from, filters.to, JSON.stringify(filters.klasse), filters.varebil, filters.omregistrering, filters.selgerPostnummer, filters.kjoperPostnummer])

  // Brand options are independent of the brand selection, so they load in
  // their own effect. Refetching them on every pick reset the list mid-use.
  useLatest(alive => {
    if (!classLabel || !MERKE_TABS.includes(tab)) return
    fetchKlasseMerker(classLabel, f)
      .then(r => { if (alive()) setMerkeOptions(r.data.rows.map(x => x.merke)) })
      .catch(() => { if (alive()) setMerkeOptions([]) })
  }, [classLabel, tab, filters.from, filters.to, JSON.stringify(filters.klasse), filters.varebil, filters.omregistrering, filters.selgerPostnummer, filters.kjoperPostnummer])

  useLatest(alive => {
    if (!classLabel) return
    setFinansiert(null)
    fetchKlasserTable(f)
      .then(r => {
        if (!alive()) return
        const row = r.data.rows.find(x => x.class_label === classLabel)
        setFinansiert(row ? row.finans_andel_pct : null)
      })
      .catch(() => { if (alive()) setFinansiert(null) })
  }, [classLabel, filters.from, filters.to, JSON.stringify(filters.klasse), filters.varebil, filters.omregistrering, filters.selgerPostnummer, filters.kjoperPostnummer])

  // Kjøperalder loads regardless of the active tab: whether the tab exists at
  // all depends on has_private_buyers, which only the response can tell us
  // (#485 AC2). It doubles as the tab's own data, so opening it costs nothing.
  useLatest(alive => {
    if (!classLabel) return
    fetchKlasseKjoperalder(classLabel, f, merkeFilter)
      .then(r => { if (alive()) setKjoperalder(r.data) })
      .catch(() => { if (alive()) setKjoperalder(null) })
  }, [classLabel, JSON.stringify(merkeFilter), filters.from, filters.to, JSON.stringify(filters.klasse), filters.varebil, filters.omregistrering, filters.selgerPostnummer, filters.kjoperPostnummer])

  // A filter change can empty the segment of private buyers while the tab is
  // open — fall back rather than leave a selected tab with nothing behind it.
  const hideKjoperalder = kjoperalder != null && !kjoperalder.has_private_buyers
  useEffect(() => {
    if (hideKjoperalder && tab === 'kjoperalder') setTab('merker')
  }, [hideKjoperalder, tab])

  const visibleTabs = TABS.filter(tab => tab.slug !== 'kjoperalder' || !hideKjoperalder)

  const backUrl = `/bruktbil/klasser?${searchParams.toString()}`

  return (
    <div className="space-y-4">
      <Link to={backUrl} className="inline-flex items-center text-sm text-primary-600 dark:text-primary-400 hover:underline">
        {t('klasser.tilbake')}
      </Link>

      <div className="card p-4 flex items-center gap-6">
        <p className="font-mono text-sm font-semibold text-gray-800 dark:text-gray-200">{classLabel}</p>
        <div data-testid="klasse-finansiert" className="ml-auto text-sm">
          <p className="text-xs text-gray-500">{t('klasser.finansiertPct')}</p>
          <p className="font-semibold">
            {finansiert != null
              ? finansiert.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
              : '—'}
          </p>
        </div>
      </div>

      <div className="flex border-b border-gray-200 dark:border-dark-border">
        {visibleTabs.map(vt => (
          <button key={vt.slug} onClick={() => setTab(vt.slug)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === vt.slug
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}>
            {t(vt.labelKey)}
          </button>
        ))}
      </div>

      {/* Merke-filter stays mounted while the profile reloads: selecting a brand
          flips `loading`, and unmounting here would close the dropdown after
          every single pick. */}
      {MERKE_TABS.includes(tab) && (
        <div className="card p-4">
          <MerkeMultiSelect
            options={merkeOptions}
            selected={merkeFilter}
            onChange={setMerkeFilter}
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {tab === 'drivstoff' ? t('detaljer.merkeHintDrivstoff') : t('detaljer.merkeHintAlder')}
          </p>
        </div>
      )}

      {loading && <div className="card p-4 animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />}

      {/* Merker tab */}
      {!loading && tab === 'merker' && merker && (
        <TopMerkerTable rows={merker.rows} />
      )}

      {/* Modeller tab — one row per merke+modell pair (#586 AC2) */}
      {!loading && tab === 'modeller' && modeller && (
        <TopModellerTable rows={modeller.rows} />
      )}

      {/* Aldersprofil biler — 1-year line chart 0–25 + "25+ år" badge */}
      {!loading && tab === 'aldersprofil' && aldersprofil && (
        <div className="space-y-4">
          <div className="card p-4 flex items-center gap-6">
            <div className="flex gap-8 text-sm">
              {[
                [t('detaljer.snitt'), aldersprofil.stats.snittalder],
                [t('detaljer.median'), aldersprofil.stats.medianalder],
              ].map(([label, val]) => (
                <div key={label as string}>
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="font-semibold">{val != null ? t('units.aar', { value: _nb.format(val as number) }) : '—'}</p>
                </div>
              ))}
            </div>
            <div className="ml-auto px-3 py-1.5 rounded-sm bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-sm font-medium">
              {t('detaljer.over25', { n: _nb.format(aldersprofil.over_25 ?? 0) })}
            </div>
          </div>
          <AldersprofilChart series={aldersprofil.series ?? []} />
          <AlderStatsGrid
            series={aldersprofil.series ?? []}
            ageLabel={t('detaljer.bilalder')}
            footnote={t('detaljer.bilalderFootnote')}
          />
        </div>
      )}

      {/* Kjøperalder — 1-year line chart 18–75 + "75+ år" badge, privatkjøp only */}
      {tab === 'kjoperalder' && kjoperalder && (
        <div className="space-y-4">
          <div className="card p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('detaljer.kunPrivatkjopere')}</p>
            <div className="flex items-center gap-6">
              <div className="flex gap-8 text-sm">
                <div>
                  <p className="text-xs text-gray-500">{t('detaljer.snittKjoperalder')}</p>
                  <p className="font-semibold">{kjoperalder.snittalder != null ? t('units.aar', { value: _nb.format(kjoperalder.snittalder) }) : '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">{t('detaljer.medianKjoperalder')}</p>
                  <p className="font-semibold">{kjoperalder.medianalder != null ? t('units.aar', { value: _nb.format(kjoperalder.medianalder) }) : '—'}</p>
                </div>
              </div>
              <div className="ml-auto px-3 py-1.5 rounded-sm bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-sm font-medium">
                {t('detaljer.over75', { n: _nb.format(kjoperalder.over_75 ?? 0) })}
              </div>
            </div>
          </div>
          <AldersprofilChart
            series={fromKjoperAlderSeries(kjoperalder.series ?? [])}
            xLabel={t('detaljer.kjoperalderAar')}
          />
          <AlderStatsGrid
            series={kjoperalder.series ?? []}
            ageLabel={t('detaljer.kjoperalder')}
            footnote={t('detaljer.kjoperalderFootnote')}
          />
        </div>
      )}

      {/* Drivstoff — raw SVV values, optionally split by merke (#586 AC5) */}
      {!loading && tab === 'drivstoff' && drivstoff && (
        <DrivstoffMatrise series={drivstoff.series ?? []} />
      )}
    </div>
  )
}
